import React, { Suspense, useEffect } from "react";
import io from "socket.io-client";
import { injectScriptString } from "./utils/injectScripts";
// import AppRouter from "./app.router";
import store from "./core/redux/store";
import { setGlobalSettings } from "./core/redux/commonSlice";
import { setConfigurableMessages } from "./core/redux/messagesSlice";
import { SOCKET_SERVER_URL } from "./utils/constants";
import { Provider } from "react-redux";
import { HelmetProvider } from "react-helmet-async";
import { Helmet } from "react-helmet-async";
import "bootstrap/dist/js/bootstrap.bundle.min.js";
import "bootstrap/dist/css/bootstrap.min.css";
import "bootstrap-daterangepicker/daterangepicker.css";
import "primereact/resources/themes/saga-blue/theme.css";
import "primereact/resources/primereact.min.css";
import "primeicons/primeicons.css";
import "react-toastify/dist/ReactToastify.css";
import "./assets/css/meanmenu.css";
import "owl.carousel/dist/assets/owl.carousel.css";
import "owl.carousel/dist/assets/owl.theme.default.css";

import "./index.css";
import "./mobile-responsive.css";
import SpinnerProvider, { LoaderComponent } from "./components/spinner/spinner";
import { ToastContainer } from "react-toastify";
import "./assets/icons/feather/css/iconfont.css";
import "./assets/icons/fontawesome/css/fontawesome.min.css";
import "./assets/icons/fontawesome/css/all.min.css";
import "./assets/icons/tabler-icons/tabler-icons.css";
import "./assets/css/main.css";
import "./assets/css/meanmenu.css";
// import SocketClient from "./services/socket/socketioClient";
// import { useReloadOnLocationPermissionChange } from "./hooks/useLocationTracking";
import { getData, putData } from "./services/api.service";
import { end_points } from "./services/endpoints";
import { setRestrictedWords } from "./utils/profanityConfig";
import GoogleTranslateProvider from "./components/google-translate/GoogleTranslateProvider";
import EnableLocationModal from "./components/common/enableLocationModal";
// import AutoLogout from "./autoLogout";
const AppRouter = React.lazy(() => import("./app.router"));
const SocketClient = React.lazy(() => import("./services/socket/socketioClient"));
const AutoLogout = React.lazy(() => import("./autoLogout"));
const unSubscribeToken = new URLSearchParams(window.location.search).get("token");
const TOAST_NOTIFICATIONS_UPDATED_CHANNEL = "toast_notifications_updated";
function App() {
  // useReloadOnLocationPermissionChange();

  const fetchGlobalScripts = async () => {
    try {
      const response = await getData(end_points.global_script_list_api.url);
      const data = response?.data?.data;

      const { headerScripts, bodyScripts } = data || {};

      if (headerScripts) {
        injectScriptString(headerScripts, "head");
      }

      if (bodyScripts) {
        injectScriptString(bodyScripts, "body");
      }
    } catch (err) {
      console.error("Global script load error", err);
    }
  };

  const fetchStripeKeys = async () => {
    try {
      const response = await getData(end_points.get_stripe_keys.url);
      const data = response?.data?.data;
      localStorage.setItem("public_key", data[0].public_key);
    } catch (err) {
      console.error("Stripe keys fetch error", err);
    }
  };
  const fetchGlobalSettings = async () => {
    try {
      const response = await getData(end_points.global_settings.url + "?type=date");
      if (response?.data?.status === 200) {
        store.dispatch(setGlobalSettings(response?.data?.data));
      }
      console.log("Global settings", response?.data?.data);
    } catch (err) {
      console.error("Global settings fetch error", err);
    }
  };

  const fetchRestrictedTags = async () => {
    try {
      const response = await getData(end_points.tags_list.url);
      if (response?.status < 400) {
        setRestrictedWords(response?.data?.data?.restricted_tags);
      }
    } catch (err) {
      console.error("Restricted tags fetch error", err);
    }
  };

  const fetchConfigurableMessages = async () => {
    try {
      const response = await getData(end_points.get_response_messages.url);
      if (response?.data?.status === 200) {
        store.dispatch(setConfigurableMessages(response?.data?.data));
      }
    } catch (err) {
      console.error("Configurable messages fetch error", err);
    }
  };

  useEffect(() => {
    const isEmbedRoute =
      typeof window !== "undefined" && window.location.pathname.startsWith("/video/embed/");
    if (!isEmbedRoute) {
      fetchGlobalScripts();
    }
    unSubscribeUpdate();
    fetchStripeKeys();
    fetchGlobalSettings();
    fetchRestrictedTags();
    fetchConfigurableMessages();
  }, []);

  // Real-time sync: whenever an admin creates/updates/deletes a Toast Notification,
  // api-gateway emits this event — refetch and repopulate the Redux message map so
  // overrides take effect immediately for every open session (logged in or not),
  // with no page reload or re-login. `getMessage()` already reads from this store on
  // every call, so no interceptor or page-level change is needed beyond this refresh.
  useEffect(() => {
    if (!SOCKET_SERVER_URL) return;

    const socket = io(SOCKET_SERVER_URL, { transports: ["websocket"] });
    const handleToastNotificationsUpdated = () => {
      fetchConfigurableMessages();
    };

    socket.on(TOAST_NOTIFICATIONS_UPDATED_CHANNEL, handleToastNotificationsUpdated);

    return () => {
      socket.off(TOAST_NOTIFICATIONS_UPDATED_CHANNEL, handleToastNotificationsUpdated);
      socket.disconnect();
    };
  }, []);

  const unSubscribeUpdate = async () => {
    // Unsubscribe links are always built as `${FRONTEND_URL}/?token=...` (see
    // about.controller.ts), so only treat a `token` query param as an
    // unsubscribe token on the homepage. Other pages — e.g. /join-golive —
    // use their own `token` query param for unrelated purposes, and running
    // this here would send that token to the unsubscribe endpoint and surface
    // a bogus "Invalid token format" error.
    if (!unSubscribeToken || window.location.pathname !== "/") return;
    const payload = {
      token: unSubscribeToken,
    };

    try {
      const response = await putData(end_points.unsubscribe_api.url, payload);

      console.log("Unsubscribe response", response?.data);
    } catch (err) {
      console.error("Unsubscribe API error", err);
    }
  };

  return (
    <>
      <HelmetProvider>
        <Helmet>
          <title>Healing Sky</title>
          <meta
            name="description"
            content="Securely log in to your Healing Sky account to manage services, access resources, and monitor your progress. Safe, reliable, and available globally anytime."
          />
          <meta property="og:type" content="website" />
          <meta property="og:site_name" content="Healing Sky" />
          <meta property="og:title" content="Healing Sky" />
          <meta
            property="og:description"
            content="Discover trusted healthcare professionals and wellness solutions."
          />
          <meta
            property="og:image"
            content="https://healingsky-dev-gbdgcedgeyg4hkhs.z02.azurefd.net/media-libary/1785257633447-7e7db7f3-1e26-4e31-8f39-7dbea41ccb7c.jpg"
          />
          <meta
            property="og:image:secure_url"
            content="https://healingsky-dev-gbdgcedgeyg4hkhs.z02.azurefd.net/media-libary/1785257633447-7e7db7f3-1e26-4e31-8f39-7dbea41ccb7c.jpg"
          />
          <meta property="og:image:type" content="image/jpeg" />
          <meta property="og:image:width" content="2503" />
          <meta property="og:image:height" content="1315" />
          <meta property="og:image:alt" content="Healing Sky – Wellness Platform" />
          <meta property="og:locale" content="en_US" />
          <meta property="og:url" content={window.location.href} />
          <meta name="twitter:card" content="summary_large_image" />
          <meta name="twitter:site" content="@HealingSky" />
          <meta name="twitter:title" content="Healing Sky" />
          <meta
            name="twitter:description"
            content="Discover trusted healthcare professionals and wellness solutions."
          />
          <meta
            name="twitter:image"
            content="https://healingsky-dev-gbdgcedgeyg4hkhs.z02.azurefd.net/media-libary/1785257633447-7e7db7f3-1e26-4e31-8f39-7dbea41ccb7c.jpg"
          />
          <meta name="twitter:image:alt" content="Healing Sky – Wellness Platform" />
        </Helmet>
        <Provider store={store}>
          <ToastContainer autoClose={2000} limit={1} position="top-center" hideProgressBar />
          {/* Google Translate — mounts once for the entire app lifetime */}
          <GoogleTranslateProvider />
          <EnableLocationModal />

          <SpinnerProvider>
            <SocketClient />
            <AutoLogout />
            {/* Wrap AppRouter with Suspense */}
            <Suspense fallback={<LoaderComponent />}>
              <AppRouter />
            </Suspense>
          </SpinnerProvider>
        </Provider>
      </HelmetProvider>
    </>
  );
}

export default App;
